/**
 * Billing / Premium management (V5 payments, SM-10b) — /settings/billing.
 *
 * Status + entitlement are SERVER-evaluated (GET /store/subscription); this
 * screen renders them and hands off to the Stripe billing portal for
 * card/cancel/resume (cancel is one link deep — no dark patterns). EC-38:
 * `premium_changed` pushes re-render this screen live.
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { copy, formatCopy } from "@humble/shared";
import { api, isApiError } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import { Button, Card, Skeleton, toast } from "../../design/index.js";
import { IconCrown } from "./sku-icons.js";
import "./store.css";

interface SubStatus {
  status: "none" | "active" | "past_due" | "cancel_scheduled" | "lapsed";
  entitled: boolean;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

const STATUS_LINE: Record<SubStatus["status"], string> = {
  none: "No subscription. You are dating at street level.",
  active: "Premium is active. The fog of war is off.",
  past_due: "Payment failed — benefits continue while we retry.",
  cancel_scheduled: "Cancellation scheduled. Premium runs to the period end, then stops.",
  lapsed: "Premium has lapsed. Your settings are kept and reactivate if you return.",
};

export function BillingScreen() {
  const [sub, setSub] = useState<SubStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/store/subscription")
      .then((res) => setSub(res as unknown as SubStatus))
      .catch(() => setSub(null));
  }, []);

  useEffect(() => {
    load();
    ws.ensureConnected();
    return ws.on("premium_changed", load); // EC-38 — re-render on lapse/renewal
  }, [load]);

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = (await api.post("/store/subscription/portal", {})) as unknown as { url: string };
      window.location.href = res.url;
    } catch (err) {
      toast(isApiError(err) ? err.message : "Could not open the billing portal.", "error");
      setBusy(false);
    }
  };

  if (sub === null) {
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", padding: 16 }}>
        <Skeleton height={34} width={220} radius={8} style={{ marginBottom: 14 }} />
        <Skeleton style={{ height: 160 }} radius={20} />
      </div>
    );
  }

  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end) : null;
  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: 16 }}>
      <p className="h-caps store__kicker">Subscription</p>
      <h1 className="type-display-3">Premium &amp; billing</h1>
      <Card
        padding="lg"
        glow={sub.entitled ? "sr" : "none"}
        style={{ marginTop: 12 }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}
          data-testid="billing-status"
        >
          <span
            className="sku__emblem"
            style={{ "--verb": "var(--sr)", width: 40, height: 40 } as CSSProperties}
            aria-hidden="true"
          >
            <IconCrown width={20} height={20} />
          </span>
          {sub.entitled ? "Premium: ON" : "Premium: OFF"}
        </div>
        <p className="text-dim" style={{ fontSize: "0.9rem" }}>
          {STATUS_LINE[sub.status]}
        </p>
        {sub.status === "past_due" && periodEnd && (
          <p className="text-dim" style={{ fontSize: "0.85rem" }} data-testid="billing-past-due">
            {formatCopy(copy.store.payment_failed, { date: periodEnd.toLocaleDateString() })}
          </p>
        )}
        {periodEnd && sub.status !== "none" && (
          <p className="text-faint" style={{ fontSize: "0.85rem" }}>
            {sub.status === "cancel_scheduled" || sub.status === "lapsed" || sub.cancel_at_period_end
              ? "Entitled until"
              : "Renews"}
            : {periodEnd.toLocaleString()}
          </p>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {sub.status !== "none" && sub.status !== "lapsed" ? (
            <Button onClick={openPortal} loading={busy} data-testid="billing-portal">
              Manage subscription
            </Button>
          ) : (
            <Link to="/store">
              <Button data-testid="billing-subscribe">Get Premium</Button>
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
}
